<template>
  <ValidationObserver slim v-slot="{ handleSubmit, invalid, errors }" vid="emails" ref="observer">
    <form @submit.prevent="handleSubmit(update)">
      <div class="flex flex-col">
        <div class="w-full">
          <label class="inline-flex items-center">
            <input
              type="checkbox"
              name="fixedCost"
              v-model="data.fixedCost"
              :disabled="submitting"
              class="form-checkbox"
            />
            <span class="ml-2 select-none font-medium text-sm whitespace-nowrap"> Fixed Cost</span>
          </label>
        </div>
        <!-- Fixed Cost -->

        <div class="flex flex-col mt-8">
          <label for="minCost" class="inline-block font-medium text-sm mb-2">Cost</label>
          <div class="flex">
            <div class="flex w-48 items-baseline">
              <label for="minCost" class="inline-block font-medium text-sm mr-2">Min</label>
              <ValidationProvider
                name="minCost"
                vid="minCost"
                rules="required|min_value:0"
                v-slot="{ failed }"
                slim
              >
                <BaseFormGroup>
                  <BaseFormInput
                    id="minCost"
                    type="number"
                    min="0"
                    step="0.01"
                    v-model.number="data.minCost"
                    :disabled="submitting"
                    :invalid="failed"
                  />
                </BaseFormGroup>
              </ValidationProvider>
            </div>
            <!-- Minimum Cost -->
            <div v-if="!data.fixedCost" class="flex w-48 items-baseline ml-8">
              <label for="maxCost" class="inline-block font-medium text-sm mr-2">Max</label>
              <ValidationProvider
                name="maxCost"
                vid="maxCost"
                :rules="{ required: data.fixedCost, min_value: data.minCost }"
                v-slot="{ failed }"
                slim
              >
                <BaseFormGroup class="w-full">
                  <BaseFormInput
                    id="maxCost"
                    type="number"
                    min="0"
                    step="0.01"
                    v-model.number="data.maxCost"
                    :disabled="submitting"
                    :invalid="failed"
                  />
                </BaseFormGroup>
              </ValidationProvider>
            </div>
            <!-- Maximum Cost -->
          </div>
          <BaseFormInlineMessage v-if="hasErrors('minCost')" danger>
            {{ errors.minCost[0] }}
          </BaseFormInlineMessage>
          <BaseFormInlineMessage v-if="hasErrors('maxCost')" danger>
            {{ errors.maxCost[0] }}
          </BaseFormInlineMessage>
        </div>

        <div class="flex mt-8 text-sm">
          <div class="flex-1">
            Profit Margin
            <template v-if="data.fixedCost === false">
              <span key="minCost" class="font-bold">{{ minPercentProfit }}%</span> -
            </template>
            <span key="maxCost" class="font-bold">{{ maxPercentProfit }}%</span>
          </div>
        </div>

        <div class="w-48 mt-8">
          <ValidationProvider
            name="grossProfitFactor"
            vid="grossProfitFactor"
            :rules="{ required: data.fixedCost }"
            v-slot="{ failed }"
            slim
          >
            <BaseFormGroup class="w-full">
              <BaseFormLabel for="grossProfitFactor"> Gross Profit Factor </BaseFormLabel>
              <BaseFormInput
                id="grossProfitFactor"
                type="number"
                min="0"
                step="0.01"
                v-model.number="data.grossProfitFactor"
                :disabled="submitting"
                :invalid="failed"
              />
              <BaseFormInlineMessage v-if="failed" danger>
                {{ errors.grossProfitFactor[0] }}
              </BaseFormInlineMessage>
            </BaseFormGroup>
          </ValidationProvider>
        </div>
        <!-- Result -->

        <div class="flex mt-8 text-sm">
          <div class="flex-1">
            Mark Up:
            <span v-if="grossProfitPercentage" class="font-bold">
              {{ grossProfitPercentage }}%
            </span>
          </div>
          <div v-if="dailyBudget" class="flex-1">
            Daily Budget:
            <span class="font-bold">
              {{ dailyBudget }}
            </span>
          </div>
        </div>

        <div class="flex space-x-2 py-1 items-baseline mt-8">
          <BaseButton primary type="submit" :disabled="submitting || invalid">
            <span v-if="!submitting">Update</span>
            <Loader v-else small inverse />
          </BaseButton>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapActions } from 'vuex';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

const defaultData = {
  fixedCost: null,
  minCost: null,
  maxCost: null,
  grossProfitFactor: 1,
};

export default {
  mixins: [veeValidateErrorsMixin],
  props: {
    campaign: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    data: { ...defaultData },
    submitting: false,
  }),
  computed: {
    budgetPerResult() {
      const { budget, isBudgetPerResult, result } = this.campaign;

      if (!budget) return 0;

      if (isBudgetPerResult) return budget;

      if (result) return budget / result;

      return 0;
    },
    maxPercentProfit() {
      const { minCost } = this.data;
      if (!this.budgetPerResult || !minCost) return 0;
      return (100 * ((this.budgetPerResult - minCost) / this.budgetPerResult)).toFixed(2);
    },
    minPercentProfit() {
      const { maxCost } = this.data;
      if (!this.budgetPerResult || !maxCost) return 0;
      return (100 * ((this.budgetPerResult - maxCost) / this.budgetPerResult)).toFixed(2);
    },
    markup() {
      const { grossProfitFactor } = this.data;
      if (!grossProfitFactor) return null;
      return (grossProfitFactor - 1) / grossProfitFactor;
    },
    grossProfitPercentage() {
      const { markup } = this;
      if (!markup) return null;
      return (100 * markup).toFixed(2);
    },
    dailyBudget() {
      const { markup } = this;
      const { dailyBudget } = this.campaign;
      if (!markup) return null;
      if (!dailyBudget) return null;
      return (dailyBudget - dailyBudget * markup).toFixed(2);
    },
  },
  created() {
    this.setData();
  },
  watch: {
    campaign: {
      handler() {
        this.setData();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions('ui/campaigns', ['updateCampaignCosting']),
    setData() {
      Object.keys(defaultData).forEach((prop) => {
        if (this.campaign[prop] !== undefined) {
          this.data[prop] = this.campaign[prop];
        }
      });
    },
    async update() {
      const submitWithErrorHandling = this.handleFormErrors(this.updateCampaignCosting);

      this.submitting = true;

      await submitWithErrorHandling({
        campaignId: this.campaign.id,
        costingData: this.data,
      });

      this.submitting = false;
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            if (this.closeOnSuccess) {
              this.$refs.modal.doClose();
            }
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
    hasErrors(field) {
      return (this.$refs?.observer?.errors?.[field] || []).length > 0;
    },
  },
};
</script>
